import React, {useEffect, useMemo, useState} from 'react';
import {useSettingsStore, useStore} from "../../../store";
import {find, get, includes, isEqual, isNil} from "lodash";
import GridView from "../../../containers/grid-view/grid-view";
import {KEYS} from "../../../constants/key";
import {URLS} from "../../../constants/url";
import Field from "../../../containers/form/field";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {Col, Row} from "react-grid-system";
import Form from "../../../containers/form/form";
import {useGetAllQuery, usePostQuery} from "../../../hooks/api";
import {getSelectOptionsListFromData, saveFile} from "../../../utils";
import Button from "../../../components/ui/button";
import {FileText, Filter, MessageCircle, Trash} from "react-feather";
import Flex from "../../../components/flex";
import config from "../../../config";
import Modal from "../../../components/modal";
import {useNavigate} from "react-router-dom";
import {request} from "../../../services/api";
import {toast} from "react-toastify";

const getAgreementListPolicy = (row) => {
    const policies = get(row, 'policy', []);
    const agreementStatus = get(row, 'status');

    if (!Array.isArray(policies) || policies.length === 0) {
        return get(row, 'policy[0]');
    }

    const sentPolicy = find(policies, (policy) =>
        isEqual(get(policy, 'fondStatus'), 'sent') ||
        isEqual(get(policy, 'status'), 'sent')
    );

    if (sentPolicy) {
        return sentPolicy;
    }

    const paidPolicy = find(policies, (policy) =>
        isEqual(get(policy, 'fondStatus'), 'paid') ||
        isEqual(get(policy, 'status'), 'paid')
    );

    if (paidPolicy) {
        return paidPolicy;
    }

    const isOnlyNew = policies.every((policy) => {
        const status = get(policy, 'fondStatus') || get(policy, 'status');
        return isEqual(status, 'new');
    });

    if (isOnlyNew || isEqual(agreementStatus, 'new')) {
        return get(policies, '[0]');
    }

    const policyWithNumber = find(
        policies,
        (policy) => get(policy, 'number') || get(policy, 'seria')
    );
    return policyWithNumber || get(policies, '[0]');
};

const renderPolicySeriaNumber = (policy) => {
    if (!policy) {
        return '-';
    }
    const seria = String(get(policy, 'seria', '') || '').trim();
    const number = String(get(policy, 'number', '') || '').trim();

    if (!seria && !number) {
        return '-';
    }
    if (seria && number) {
        return (
            <>
                {seria}
                <br />
                {number}
            </>
        );
    }
    return seria || number;
};

const AgreementsContainer = () => {
    const {t} = useTranslation()
    const user = useStore(state => get(state, 'user'))
    const [productGroupId, setProductGroupId] = useState(null);
    const [productSubGroupId, setProductSubGroupId] = useState(null);
    const [createdAtFrom, setCreatedAtFrom] = useState(null);
    const [createdAtTo, setCreatedAtTo] = useState(null);
    const [branch, setBranch] = useState(null);
    const [pagination, setPagination] = useState({page:1,limit:15});
    const [tr, setTr] = useState(null);
    const navigate = useNavigate();
    const [filter, setFilter] = useState({
        branch: get(user, 'branch._id'),
    });
    const setBreadcrumbs = useStore(state => get(state, 'setBreadcrumbs', () => {
    }))
    const breadcrumbs = useMemo(() => [
        {
            id: 1,
            title: t("Agreements"),
            path: '/agreements',
        },
    ], [])
    const resetInsurer = useSettingsStore(state => get(state, 'resetInsurer', () => {
    }))
    const resetBeneficiary = useSettingsStore(state => get(state, 'resetBeneficiary', () => {
    }))
    const resetAgreement = useSettingsStore(state => get(state, 'resetAgreement', () => {
    }))
    const resetPledger = useSettingsStore(state => get(state, 'resetPledger', () => {
    }))
    const resetObjects = useSettingsStore(state => get(state, 'resetObjects', () => {
    }))

    let {data: groups} = useGetAllQuery({key: KEYS.groupsofproducts, url: `${URLS.groupsofproducts}/list`})
    groups = getSelectOptionsListFromData(get(groups, `data.data`, []), '_id', 'name')

    let {data: subGroups} = useGetAllQuery({
        key: KEYS.subgroupsofproductsFilter,
        url: URLS.subgroupsofproductsFilter,
        params: {
            params: {
                group: productGroupId
            }
        },
        enabled: !!productGroupId
    })
    subGroups = getSelectOptionsListFromData(get(subGroups, `data.data`, []), '_id', 'name')

    let {data: products} = useGetAllQuery({
        key: [KEYS.productsfilter, productSubGroupId],
        url: URLS.products,
        params: {
            params: {
                subGroup: productSubGroupId
            }
        },
        enabled: !!productSubGroupId
    })
    const productsList = getSelectOptionsListFromData(get(products, `data.data`, []), '_id', 'name')
    let {data: branches} = useGetAllQuery({
        key: KEYS.branches, url: `${URLS.branches}/list`, params: {
            params: {
                limit: 100
            }
        }
    })
    branches = getSelectOptionsListFromData(get(branches, `data.data`, []), '_id', 'branchName')

    const fetchPortfelReport = async (getValues) => {
        const vals = (getValues && getValues()) || {};
        try {
            const {data} = await request.get(URLS.agreementPortfelReport, {
                params: {
                    ...vals,
                    branch: includes([config.ROLES.admin], get(user, 'role.name'))
                        ? (vals.branch ?? branch)
                        : get(user, 'branch._id'),
                    page: get(pagination, 'page', 1),
                    limit: get(pagination, 'limit', 15),
                    subGroup: vals.subGroup ?? productSubGroupId,
                    group: vals.group ?? productGroupId,
                },
                responseType: 'blob',
            });
            saveFile(data);
        } catch (e) {
            toast.error(get(e, 'response.data.message') || `ERROR!!! ${URLS.agreementPortfelReport} api not working`);
        }
    };

    let {refetch} = useGetAllQuery({
        key: KEYS.agreementWeeklyReport,
        url: URLS.agreementWeeklyReport,
        params: {
            params: {
                createdAtFrom: createdAtFrom ? dayjs(createdAtFrom).format("YYYY-MM-DD") : undefined,
                createdAtTo: createdAtTo ? dayjs(createdAtTo).format("YYYY-MM-DD") : undefined,
                branch: includes([config.ROLES.admin], get(user, 'role.name')) ? branch : get(user, 'branch._id'),
            },
            responseType: 'blob',
        },
        enabled: false,
        cb: {
            success: (res) => {
                saveFile(res)
            }
        }
    })
    let {refetch:carReportRefetch} = useGetAllQuery({
        key: KEYS.agreementCarReport,
        url: URLS.agreementCarReport,
        params: {
            params: {
                createdAtFrom: createdAtFrom ? dayjs(createdAtFrom).format("YYYY-MM-DD") : undefined,
                createdAtTo: createdAtTo ? dayjs(createdAtTo).format("YYYY-MM-DD") : undefined,
                branch: includes([config.ROLES.admin], get(user, 'role.name')) ? branch : get(user, 'branch._id'),
            },
            responseType: 'blob'
        },
        enabled: false,
        cb: {
            success: (res) => {
                saveFile(res)
            }
        }
    })
    let {refetch:transactionReportRefetch} = useGetAllQuery({
        key: KEYS.transactionReceiptsReport,
        url: URLS.transactionReceiptsReport,
        params: {
            params: {
                fromDate: createdAtFrom ? dayjs(createdAtFrom).format("YYYY-MM-DD") : undefined,
                toDate: createdAtTo ? dayjs(createdAtTo).format("YYYY-MM-DD") : undefined,
            },
            responseType: 'blob'
        },
        enabled: false,
        cb: {
            success: (res) => {
                saveFile(res, 'transactions-report')
            }
        }
    })
    const {mutate:annualRequest,isLoading:isLoadingAnnual} = usePostQuery({listKeyId:[KEYS.agreements, filter]})

    useEffect(() => {
        setBreadcrumbs(breadcrumbs)
        resetInsurer()
        resetBeneficiary()
        resetAgreement()
        resetPledger()
        resetObjects()
    }, [])
    return (
        <>
            <GridView
                getPagination={(val)=>setPagination(val)}
                tableHeaderData={[
                    {
                        id: 1,
                        key: 'agreementNumber',
                        title: t("agreementNumber")
                    },
                    {
                        id: 2,
                        key: 'agreementDate',
                        title: t("agreementDate"),
                        render: (val) => dayjs(get(val, 'agreementDate')).format("DD.MM.YYYY")
                    },
                    {
                        id: 3,
                        key: 'insurant',
                        title: t("Страхователь"),
                        render: (_tr) => get(_tr, 'insurant.organization.name', `${get(_tr, 'insurant.person.fullName.lastname', '-')} ${get(_tr, 'insurant.person.fullName.firstname', '-')} ${get(_tr, 'insurant.person.fullName.middlename', '-')}`)
                    },
                    {
                        id: 4,
                        key: 'beneficiary',
                        title: t("Выгодоприобретатель"),
                        render: (_tr) => get(_tr, 'beneficiary.organization.name', `${get(_tr, 'beneficiary.person.fullName.lastname', '-')} ${get(_tr, 'beneficiary.person.fullName.firstname', '-')} ${get(_tr, 'beneficiary.person.fullName.middlename', '-')}`)
                    },
                    {
                        id: 5,
                        key: 'policy[0].issueDate',
                        title: t("startOfInsurance"),
                        date: true
                    },
                    {
                        id: 6,
                        key: 'endOfInsurance',
                        title: t("endOfInsurance"),
                        date: true
                    },
                    {
                        id: 7,
                        key: 'product.name',
                        title: t("Наименование продукта")
                    },
                    {
                        id: 8,
                        key: 'totalInsuranceSum',
                        title: t("totalInsuranceSum"),
                        hasNumberFormat: true
                    },
                    {
                        id: 9,
                        key: 'totalInsurancePremium',
                        title: t("totalInsurancePremium"),
                        hasNumberFormat: true
                    },
                    {
                        id: 99,
                        key: 'policy[0].number',
                        title: t("Номер полиса"),
                        render: (row) => renderPolicySeriaNumber(getAgreementListPolicy(row)),
                    },
                    {
                        id: 10,
                        key: 'status',
                        title: t("Status"),
                    },
                    {
                        id: 11,
                        key: 'createdAt',
                        title: t("Дата ввода в систему"),
                        render: (val) => dayjs(get(val, 'createdAt')).format("DD.MM.YYYY HH:mm")
                    },
                ]}
                params={{...filter}}
                keyId={[KEYS.agreements, filter]}
                url={URLS.agreements}
                listUrl={`${URLS.agreements}/list`}
                title={t('Agreements')}
                responseDataKey={'data.data'}
                viewUrl={'/agreements/view'}
                viewIsTab
                createUrl={'/agreements/create'}
                updateUrl={'/agreements/edit'}
                hideDeleteBtn={!includes([config.ROLES.admin],get(user,'role.name'))}
                isHideColumn
                checkStatus
                extraActions={(_tr)=>includes([config.ROLES.admin],get(user,'role.name')) && <MessageCircle onClick={()=>setTr(_tr)} size={22} style={{marginLeft:10,cursor:'pointer',color:'#306962'}}/>}
                                                  extraFilters={<Form sm formRequest={({data: { ...rest} = {}}) => {
                    setFilter(rest);
                }}
                                    mainClassName={'mt-15'}>

                    {({getValues}) => <Row align={'end'} gutterWidth={10}>
                        <Col xs={1.25}>
                            <Field sm property={{onChange: (val) => setBranch(val)}} type={'select'}
                                   label={t("Филиал")} name={'branch'}
                                   options={branches} defaultValue={get(filter, 'branch')}
                                   isDisabled={!includes([config.ROLES.admin], get(user, 'role.name'))}/>
                            <Field sm label={t('agreementnumber')} type={'input'}
                                   name={'agreementNumber'}
                                   defaultValue={get(filter, 'agreementNumber')}

                            />
                            <Field sm label={t('Номер полиса')} type={'input'}
                                   name={'policyNumber'}
                                   defaultValue={get(filter, 'policyNumber')}

                            />
                        </Col>
                        <Col xs={1}>
                            <Field sm label={t('Дата соглашения от')} type={'datepicker'}
                                   name={'agreementDateFrom'}
                                   defaultValue={get(filter, 'agreementDateFrom')}

                            />
                            <Field sm label={t('Дата соглашения до')} type={'datepicker'}
                                   name={'agreementDateTo'}
                                   defaultValue={get(filter, 'agreementDateTo')}

                            />
                        </Col>

                        <Col xs={1.5}>
                            <Field sm label={t('Страхователь')} type={'input'}
                                   name={'insurant'}
                                   defaultValue={get(filter, 'insurant')}

                            />
                            <Field  sm label={t('Выгодоприобретатель')} type={'input'}
                                    name={'beneficiary'}
                                    defaultValue={get(filter, 'beneficiary')}

                            />
                        </Col>

                        <Col xs={1}>
                            <Field sm label={t('Начало периода страхования от')} type={'datepicker'}
                                   name={'startOfInsuranceFrom'}
                                   defaultValue={get(filter, 'startOfInsuranceFrom')}

                            />
                            <Field sm label={t('Начало периода страхования до')} type={'datepicker'}
                                   name={'startOfInsuranceTo'}
                                   defaultValue={get(filter, 'startOfInsuranceTo')}

                            />
                        </Col>

                        <Col xs={1}>
                            <Field sm label={t('Конец периода страхования от')} type={'datepicker'}
                                   name={'endOfInsuranceFrom'}
                                   defaultValue={get(filter, 'endOfInsuranceFrom')}

                            />
                            <Field sm label={t('Конец периода страхования до')} type={'datepicker'}
                                   name={'endOfInsuranceTo'}
                                   defaultValue={get(filter, 'endOfInsuranceTo')}

                            />
                        </Col>

                        <Col xs={1.5}>
                            <Field sm label={t('Выберите категорию')} options={groups} type={'select'}
                                   name={'group'}
                                   property={{onChange: (val) => setProductGroupId(val)}}
                                   defaultValue={productGroupId}
                            />
                            <Field sm label={t('Bыберите подкатегорию')} options={subGroups} type={'select'}
                                   name={'subGroup'}
                                   property={{onChange: (val) => setProductSubGroupId(val)}}
                                   defaultValue={productSubGroupId}
                            />
                            <Field sm label={t('Выберите продукта')} options={productsList} type={'select'}
                                   name={'product'}
                                   defaultValue={get(filter, 'product')}
                            />
                        </Col>


                        <Col xs={1.25}>
                            <Field sm label={t('Общая страховая премия от')} type={'number-format-input-filter'}
                                   name={'totalInsurancePremiumFrom'}
                                   defaultValue={get(filter, 'totalInsurancePremiumFrom', null)}
                            />
                            <Field sm label={t('Общая страховая премия до')} type={'number-format-input-filter'}
                                   name={'totalInsurancePremiumTo'}
                                   defaultValue={get(filter, 'totalInsurancePremiumTo', null)}
                            />
                        </Col>
                        <Col xs={1.25}>
                            <Field sm label={t('Общая страховая сумма от')} type={'number-format-input-filter'}
                                   name={'totalInsuranceSumFrom'}
                                   defaultValue={get(filter, 'totalInsuranceSumFrom', null)}

                            />
                            <Field sm label={t('Общая страховая сумма до')} type={'number-format-input-filter'}
                                   name={'totalInsuranceSumTo'}
                                   defaultValue={get(filter, 'totalInsuranceSumTo', null)}
                            />
                        </Col>

                        <Col xs={1.25}>
                            <Field sm type={'select'} label={t("Status")} name={'status'}
                                   options={[{value: 'new', label: 'new'}, {
                                       value: 'partialPaid',
                                       label: 'partialPaid'
                                   }, {value: 'paid', label: 'paid'}, {value: 'sent', label: 'sent'}, {
                                       value: 'terminated',
                                       label: 'terminated'
                                   }, {value: 'annulled', label: 'annulled'}]}
                                   defaultValue={get(filter, 'status')}
                            />
                            <Field sm property={{onChange: (val) => {
                                    setCreatedAtFrom(val)
                                }}} label={t('Дата создания от')}
                                   type={'datepicker'}
                                   name={'createdAtFrom'}
                                   defaultValue={get(filter, 'createdAtFrom')}

                            />
                            <Field sm property={{onChange: (val) => {
                                    setCreatedAtTo(val)
                                }}} label={t('Дата создания до')}
                                   type={'datepicker'}
                                   name={'createdAtTo'}
                                   defaultValue={get(filter, 'createdAtTo')}

                            />
                        </Col>

                        <Col xs={1}>
                            <div>
                                <Button xs htmlType={'submit'}><Flex justify={'center'}><Filter size={14}/><span
                                    style={{marginLeft: '5px'}}>{t("Применить")}</span></Flex></Button>
                                <Button className={'mt-15'} xs onClick={() => {
                                    navigate(0)
                                }}  danger type={'reset'}><Flex justify={'center'}><Trash
                                    size={14}/><span
                                    style={{marginLeft: '5px'}}>{t("Очистить")}</span></Flex></Button>
                                <Button xs onClick={() => {
                                   refetch()
                                }} className={'mt-15 mb-15'} yellow type={'button'}><Flex justify={'center'}><FileText
                                    size={14}/><span
                                    style={{marginLeft: '5px'}}>{t("Отчет")}</span></Flex></Button>
                                <Button xs onClick={() => {
                                    fetchPortfelReport(getValues)
                                }} className={'mb-15'} green type={'button'}><Flex justify={'center'}><FileText
                                    size={14}/><span
                                    style={{marginLeft: '5px'}}>{t("Portfel report")}</span></Flex></Button>
                                <Button xs onClick={() => {
                                    carReportRefetch()
                                }} className={'mb-15'} green type={'button'}><Flex justify={'center'}><FileText
                                    size={14}/><span
                                    style={{marginLeft: '5px'}}>{t("Отчет по ТС")}</span></Flex></Button>
                                <Button xs onClick={() => {
                                    transactionReportRefetch()
                                }} className={'mb-15'} green type={'button'}><Flex justify={'center'}><FileText
                                    size={14}/><span
                                    style={{marginLeft: '5px'}}>{t("Отчет о транзакциях")}</span></Flex></Button>
                            </div>
                        </Col>
                    </Row>}
                </Form>}
            />
            <Modal title={'Annual'} visible={!isNil(tr)}
                   hide={() => setTr(null)}>
                <br/>
                <Form formRequest={({data:attrs})=>{
                    annualRequest({url:`/api/agreement/annual/${get(tr,'_id')}`,attributes:{...attrs}},{
                        onSuccess:()=>{
                            setTr(null)
                        }
                    })
                }} footer={<Button type={"submit"} block>{t("Send")}</Button>}>
                 <Row>
                     <Col xs={12}>
                         <Field params={{required:true}} label={'Reason'} name={'reason'} type={'textarea'} />
                     </Col>

                 </Row>
                </Form>
            </Modal>
        </>
    );
};

export default AgreementsContainer;
